import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { briefingIntentAPI } from '../../api/briefingIntent';
import { monitoringIntentAPI } from '../../api/monitoringIntent';
import { clearBriefingIntent, clearBriefingToken, getBriefingIntent, getBriefingToken, setBriefingIntent, type BriefingIntent } from '../../utils/briefingIntent';
import { clearMonitoringIntent, clearMonitoringToken, getMonitoringIntent, getMonitoringToken, setMonitoringIntent, type MonitoringIntent } from '../../utils/monitoringIntent';
import { clearPricingSelection, getPendingPricingSelection, parsePricingSelection, pricingUpgradePath } from '../../utils/pricingIntent';

function safeReturnPath(from: unknown): string {
  if (!from || typeof from !== 'object') return '/dashboard';
  const value = from as { pathname?: unknown; search?: unknown; hash?: unknown };
  const path = typeof value.pathname === 'string' ? value.pathname : '';
  if (!path.startsWith('/') || path.startsWith('//') || path === '/auth/continue') return '/dashboard';
  return path
    + (typeof value.search === 'string' && value.search.startsWith('?') ? value.search : '')
    + (typeof value.hash === 'string' && value.hash.startsWith('#') ? value.hash : '');
}

async function resumeBriefing(token: string | null, pendingResult?: { intent: BriefingIntent | null }): Promise<string | null> {
  let result = token ? await briefingIntentAPI.claim(token) : pendingResult ?? await briefingIntentAPI.pending();
  if (!token) {
    const local = getBriefingIntent();
    if (local && local.id === result.intent?.id && (local.state === 'completed' || local.state === 'dismissed')) {
      await briefingIntentAPI[local.state === 'completed' ? 'complete' : 'dismiss'](local.id);
      clearBriefingIntent();
      result = await briefingIntentAPI.pending();
    } else if (local && local.id === result.intent?.id && local.state === 'project_created' && local.project_id) {
      try {
        result = await briefingIntentAPI.projectCreated(local.id, local.project_id) as typeof result;
      } catch {
        result = { intent: local };
      }
    } else if (local && local.id !== result.intent?.id) {
      clearBriefingIntent();
    }
  }
  clearBriefingToken();
  if (!result.intent) return null;
  setBriefingIntent(result.intent);
  return result.intent.project_id
    ? `/briefings/${result.intent.project_id}?createBrief=1`
    : '/briefings?create=1';
}

async function resumeMonitoring(token: string | null, pendingResult?: { intent: MonitoringIntent | null }): Promise<string | null> {
  let result = token ? await monitoringIntentAPI.claim(token) : pendingResult ?? await monitoringIntentAPI.pending();
  if (!token) {
    const local = getMonitoringIntent();
    if (local && local.id === result.intent?.id && (local.state === 'completed' || local.state === 'dismissed')) {
      await monitoringIntentAPI[local.state === 'completed' ? 'complete' : 'dismiss'](local.id);
      clearMonitoringIntent();
      result = await monitoringIntentAPI.pending();
    } else if (local && local.id === result.intent?.id && local.state === 'project_created' && local.project_id) {
      try {
        result = await monitoringIntentAPI.projectCreated(local.id, local.project_id);
      } catch {
        return `/monitoring/${local.project_id}`;
      }
    } else if (local && local.id !== result.intent?.id) {
      clearMonitoringIntent();
    }
  }
  clearMonitoringToken();
  if (!result.intent) return null;
  if (result.intent.project_id) {
    // Creation is the last monitoring setup step. Keep a local completion
    // checkpoint if the network fails so a later sign-in cannot reopen it.
    setMonitoringIntent({ ...result.intent, state: 'completed' });
    try {
      await monitoringIntentAPI.complete(result.intent.id);
      clearMonitoringIntent();
    } catch {
      // Retried the next time AuthContinue sees the pending server intent.
    }
    return `/monitoring/${result.intent.project_id}`;
  }
  setMonitoringIntent(result.intent);
  return '/monitoring?create=1';
}

export default function AuthContinue() {
  const navigate = useNavigate();
  const location = useLocation();
  const started = useRef(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    // An explicit pricing journey takes precedence over older project setup.
    // Use the fixed billing path even if router state was supplied externally.
    const from = location.state?.from;
    const pricingSelection = (from?.pathname === '/admin/billing/upgrade'
      ? parsePricingSelection(typeof from.search === 'string' ? from.search : '') : null)
      ?? getPendingPricingSelection();
    if (pricingSelection) {
      // Consume before navigation: a permission guard may deny billing before
      // UpgradePlan mounts. The destination query preserves reload/retry.
      clearPricingSelection();
      navigate(pricingUpgradePath(pricingSelection), { replace: true });
      return;
    }
    const run = async () => {
      let flow: 'briefing' | 'monitoring' = 'briefing';
      try {
        const monitoringToken = getMonitoringToken();
        const briefingToken = getBriefingToken();
        let monitoringPending: { intent: MonitoringIntent | null } | undefined;
        let briefingPending: { intent: BriefingIntent | null } | undefined;
        let order: Array<'briefing' | 'monitoring'>;
        if (monitoringToken || briefingToken) {
          order = monitoringToken ? ['monitoring', 'briefing'] : ['briefing', 'monitoring'];
        } else {
          [monitoringPending, briefingPending] = await Promise.all([
            monitoringIntentAPI.pending(), briefingIntentAPI.pending(),
          ]);
          const monitoringTime = Date.parse(monitoringPending.intent?.created_at || '') || 0;
          const briefingTime = Date.parse(briefingPending.intent?.created_at || '') || 0;
          order = monitoringTime > briefingTime ? ['monitoring', 'briefing'] : ['briefing', 'monitoring'];
        }
        for (const kind of order) {
          flow = kind;
          const path = kind === 'monitoring'
            ? await resumeMonitoring(monitoringToken, monitoringPending)
            : await resumeBriefing(briefingToken, briefingPending);
          if (path) {
            navigate(path, { replace: true });
            return;
          }
        }
        navigate(safeReturnPath(location.state?.from), { replace: true });
      } catch (err) {
        const status = (err as { status?: number })?.status;
        if (status === 409 || status === 410) {
          if (flow === 'monitoring') clearMonitoringIntent();
          else clearBriefingIntent();
          setError(status === 409
            ? `This ${flow} link belongs to another account. Please start a new request.`
            : `This ${flow} link has expired. Please enter your details again.`);
          return;
        }
        if (!getBriefingToken() && !getBriefingIntent() &&
            !getMonitoringToken() && !getMonitoringIntent() && location.state?.from) {
          navigate(safeReturnPath(location.state?.from), { replace: true });
          return;
        }
        setError(err instanceof Error ? err.message : `Could not resume your ${flow} setup.`);
      }
    };
    void run();
  }, [navigate, location.state]);

  if (error) {
    return <main style={{ margin: '10vh auto', maxWidth: 480, padding: 24 }}>
      <h1>We couldn’t resume your setup</h1>
      <p>{error}</p>
      <button onClick={() => { setError(''); started.current = false; window.location.reload(); }}>Try again</button>
      <p><Link to="/monitoring">Go to Monitoring</Link></p>
      <p><Link to="/briefings">Go to Briefings</Link></p>
      <p><Link to="/dashboard">Continue to dashboard</Link></p>
      <p><a href="https://lazysem.com/features/monitoring-service">Enter a monitoring URL again</a></p>
      <p><a href="https://lazysem.com/features/briefing">Enter briefing details again</a></p>
    </main>;
  }
  return <main aria-busy="true">Preparing your setup…</main>;
}
